// src/services/api.js
// ─────────────────────────────────────────────────────────────────────────────
// Paths match analytics.controller.ts exactly.
// Controller: @Controller('analytics')  +  main.ts: app.setGlobalPrefix('api/v1')
// → All routes live under: http://localhost:3005/api/v1/analytics/...
// ─────────────────────────────────────────────────────────────────────────────

const BASE_URL = (
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  'http://localhost:3005/api/v1'
).replace(/\/$/, '');

// ── Auth token ────────────────────────────────────────────────────────────────
function getAuthToken() {
  try {
    return (
      sessionStorage.getItem('auth_token') ||
      sessionStorage.getItem('directus_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('directus_token') ||
      null
    );
  } catch { return null; }
}

// ── Core fetch ────────────────────────────────────────────────────────────────
export async function apiFetch(path, options = {}) {
  const token  = getAuthToken();
  const url    = `${BASE_URL}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (err) {
    throw new Error(`Network error — backend unreachable at ${BASE_URL}. (${err.message})`);
  }

  // NestJS sends back HTML for unmatched routes — detect before JSON.parse
  const ct = response.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new Error(
      `Got ${ct || 'no content-type'} from ${url} (HTTP ${response.status}). ` +
      `Verify REACT_APP_API_URL and that the backend is running.`
    );
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
  }
  return data;
}

function qs(params) {
  const str = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return str ? `?${str}` : '';
}

// ═══════════════════════════════════════════════════════════════════════════════
// dashboardAPI
// All paths match @Controller('analytics') routes in analytics.controller.ts
// ═══════════════════════════════════════════════════════════════════════════════
export const dashboardAPI = {

  // @Get('dashboard')  → /api/v1/analytics/dashboard
  getDashboardSnapshot(range = '30d', topCreatorsLimit = 10) {
    return apiFetch(`/analytics/dashboard${qs({ range, topCreatorsLimit })}`);
  },

  // @Get('dashboard/overview')  → /api/v1/analytics/dashboard/overview
  getOverview(range = '30d') {
    return apiFetch(`/analytics/dashboard/overview${qs({ range })}`);
  },

  // @Get('users')  → /api/v1/analytics/users
  getUserAnalytics(range = '30d') {
    return apiFetch(`/analytics/users${qs({ range })}`);
  },

  // @Get('content')  → /api/v1/analytics/content
  getContentAnalytics(range = '30d') {
    return apiFetch(`/analytics/content${qs({ range })}`);
  },

  // @Get('dashboard/ai')  → /api/v1/analytics/dashboard/ai
  getAiAnalytics(range = '30d') {
    return apiFetch(`/analytics/dashboard/ai${qs({ range })}`);
  },

  // @Get('top-creators')  → /api/v1/analytics/top-creators
  getTopCreators(limit = 10, range = '30d') {
    return apiFetch(`/analytics/top-creators${qs({ limit, range })}`);
  },

  // @Get('hourly-engagement')  → /api/v1/analytics/hourly-engagement
  getHourlyEngagement(range = '30d') {
    return apiFetch(`/analytics/hourly-engagement${qs({ range })}`);
  },

  // @Get('messages')  → /api/v1/analytics/messages
  getMessageAnalytics(range = '30d') {
    return apiFetch(`/analytics/messages${qs({ range })}`);
  },

  // Engagement analytics pages
  getConnectionsAnalytics(range = '30d') {
    return apiFetch(`/analytics/connections${qs({ range })}`);
  },

  getMessagesAnalytics(range = '30d') {
    return apiFetch(`/analytics/messages${qs({ range })}`);
  },

  getCommunitiesAnalytics(range = '30d') {
    return apiFetch(`/analytics/communities${qs({ range })}`);
  },

  // @Get('posts/timeline')  → /api/v1/analytics/posts/timeline
  getPostsTimeline(range = '30d') {
    return apiFetch(`/analytics/posts/timeline${qs({ range })}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// adminAPI
// ═══════════════════════════════════════════════════════════════════════════════
export const adminAPI = {

  // @Get('admin/system-health')  → /api/v1/analytics/admin/system-health
  getSystemHealth() {
    return apiFetch('/analytics/admin/system-health');
  },

  // @Get('admin/activity-log')  → /api/v1/analytics/admin/activity-log
  getActivityLog() {
    return apiFetch('/analytics/admin/activity-log');
  },
};

export const moderationAPI = {
  // Posts
  hidePost(postId) {
    return apiFetch(`/admin/moderation/posts/${postId}/hide`, { method: 'PATCH' });
  },
  restorePost(postId) {
    return apiFetch(`/admin/moderation/posts/${postId}/restore`, { method: 'PATCH' });
  },

  // Reports
  listReports(params = {}) {
    return apiFetch(`/admin/moderation/reports${qs(params)}`);
  },
  getReport(id) {
    return apiFetch(`/admin/moderation/reports/${id}`);
  },
  updateReport(id, body) {
    return apiFetch(`/admin/moderation/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  deleteReportedContent(id) {
    return apiFetch(`/admin/moderation/reports/${id}/content`, { method: 'DELETE' });
  },
  warnUser(id) {
    return apiFetch(`/admin/moderation/reports/${id}/warn-user`, { method: 'POST' });
  },
  banUserFromReport(id) {
    return apiFetch(`/admin/moderation/reports/${id}/ban-user`, { method: 'POST' });
  },

  // Blocks
  listAllBlocks(page = 1, limit = 20) {
    return apiFetch(`/admin/moderation/blocks${qs({ page, limit })}`);
  },
  forceUnblock(blockId) {
    return apiFetch(`/admin/moderation/blocks/${blockId}`, { method: 'DELETE' });
  },

  // Users
  searchUsers(search = '', page = 1) {
    return apiFetch(`/admin/moderation/users${qs({ search, page })}`);
  },
  getUserDetail(userId) {
    return apiFetch(`/admin/moderation/users/${userId}/detail`);
  },
  suspendUser(userId) {
    return apiFetch(`/admin/moderation/users/${userId}/suspend`, { method: 'PATCH' });
  },
  banUser(userId) {
    return apiFetch(`/admin/moderation/users/${userId}/ban`, { method: 'PATCH' });
  },
  reinstateUser(userId) {
    return apiFetch(`/admin/moderation/users/${userId}/reinstate`, { method: 'PATCH' });
  },

  // Auto-hide
  getAutoHideCandidates(threshold = 3) {
    return apiFetch(`/admin/moderation/auto-hide-candidates${qs({ threshold })}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Debug — open browser console and run: debugAPI()
// ═══════════════════════════════════════════════════════════════════════════════
export async function debugAPI() {
  console.group('🍀 Beetroot API Debug — Base:', BASE_URL);

  const tests = [
    ['Snapshot (30d)',   () => dashboardAPI.getDashboardSnapshot('30d', 5)],
    ['Snapshot (1y)',    () => dashboardAPI.getDashboardSnapshot('1y',  5)],
    ['Overview',        () => dashboardAPI.getOverview('30d')],
    ['Users',           () => dashboardAPI.getUserAnalytics('30d')],
    ['Content',         () => dashboardAPI.getContentAnalytics('30d')],
    ['AI',              () => dashboardAPI.getAiAnalytics('30d')],
    ['Top Creators',    () => dashboardAPI.getTopCreators(5, '30d')],
    ['Hourly',          () => dashboardAPI.getHourlyEngagement('30d')],
    ['System Health',   () => adminAPI.getSystemHealth()],
    ['Activity Log',    () => adminAPI.getActivityLog()],
  ];

  for (const [label, fn] of tests) {
    try {
      const res = await fn();
      const ok  = res?.success !== false;
      console[ok ? 'log' : 'warn'](`${ok ? '✅' : '⚠️'} ${label}:`, res);
    } catch (e) {
      console.error(`❌ ${label}:`, e.message);
    }
  }

  console.groupEnd();
}

if (typeof window !== 'undefined') {
  window.debugAPI        = debugAPI;
  window.beetrootAPI     = { dashboardAPI, adminAPI,  };
}

export const partnersAPI = {
  // Applications queue (Tab A)
  listApplications(params = {}) {
    return apiFetch(`/admin/partner-applications${qs(params)}`);
  },
  getApplication(id) {
    return apiFetch(`/admin/partner-applications/${id}`);
  },
  approveApplication(id, body) {
    return apiFetch(`/admin/partner-applications/${id}/approve`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  rejectApplication(id, body) {
    return apiFetch(`/admin/partner-applications/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  // Active partners (Tab B)
  listPartners(params = {}) {
    return apiFetch(`/admin/partners${qs(params)}`);
  },
  createPartner(body) {
    return apiFetch(`/admin/partners`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updatePartner(id, body) {
    return apiFetch(`/admin/partners/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  pausePartner(id) {
    return apiFetch(`/admin/partners/${id}/pause`, { method: 'PATCH' });
  },
  deletePartner(id) {
    return apiFetch(`/admin/partners/${id}`, { method: 'DELETE' });
  },

  // Analytics (Tab C)
  getPartnerAnalytics(range = '90d') {
    return apiFetch(`/admin/partners/analytics${qs({ range })}`);
  },
};